import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  TextField,
  Box,
  Grid,
  Typography,
  Divider,
  Link,
  IconButton,
  Snackbar,
} from '@mui/material';
import { makeStyles } from "@mui/styles";
import { Helmet } from "react-helmet";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Button from "../../../components/authentication/components/CustomButtons/Button.js";
import Card from "../../../components/authentication/components/Card/Card.js";
import CardBody from "../../../components/authentication/components/Card/CardBody.js";
import styles from "../../../components/authentication/assets/jss/labs/views/loginPage.js";
import backgroundImage from "../../../components/authentication/assets/img/bg.jpg";
import logoImage from "../../../assets/img/labasservice1.png";
import { RemoveScrollBar } from "react-remove-scroll-bar";
import ReactLoading from "react-loading";

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });

const useStyles = makeStyles(styles);

function Progress() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      zIndex: 9999
    }}>
      <ReactLoading
        type="spokes"
        color="#000099"
        height={100}
        width={50}
      />
    </div>
  );
}

export default function LoginPage() {
  const [cardAnimaton, setCardAnimation] = React.useState("cardHidden");
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    email: "",
    password: "",
    errorMessage: "",
    submitted: false,
  });

  const googleButton = useRef(null);

  useEffect(() => {
    localStorage.clear();
    setTimeout(() => setCardAnimation(""), 700);
  }, []);

  useEffect(() => {
    if (state.submitted) {
      const timer = setTimeout(() => {
        if (state.errorMessage) {
          setState(prev => ({ ...prev, submitted: false }));
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.submitted, state.errorMessage]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const showError = useCallback((message, { resetCredentials = false } = {}) => {
    setState(prev => ({
      ...prev,
      submitted: false,
      errorMessage: message,
      ...(resetCredentials ? { email: "", password: "" } : {}),
    }));
    setOpen(true);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setState(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, submitted: true, errorMessage: "" }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/adminlogin/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: state.email,
          password: state.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.email?.[0] || 
          data.password?.[0] || 
          data.detail || 
          "Login failed"
        );
      }

      if (data.tokens?.access && data.email && data.username) {
        localStorage.setItem("user", data.username);
        localStorage.setItem("email", data.email);
        localStorage.setItem("group", data.group);
        localStorage.setItem("token", data.tokens.access);

        // const userResponse = await fetch(
        //   `${process.env.REACT_APP_API_URL}/api/user_management/user_list/`,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${data.tokens.access}`,
        //     },
        //   }
        // );

        // const userData = await userResponse.json();
        // localStorage.setItem("user_access", userData.detail);
        
        // Redirect to dashboard after successful login
        window.location.href = "/app/dashboard";
      }
    } catch (error) {
      console.error("Login error:", error);
      showError(error.message, { resetCredentials: true });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmitClick(e);
    }
  };

  useEffect(() => {
    const src = 'https://accounts.google.com/gsi/client';
    const CLIENT_ID = `${process.env.REACT_APP_CLIENTID}`;

    if (!CLIENT_ID) {
      console.error("Google Client ID is not configured");
      showError("Google sign-in is currently unavailable. Please contact the administrator.");
      return;
    }

    loadScript(src)
      .then(() => {
        if (window.google?.accounts?.id) {
          window.google.accounts.id.initialize({
            client_id: CLIENT_ID,
            callback: handleCredentialResponse,
          });
          window.google.accounts.id.renderButton(googleButton.current, {
            theme: 'outline',
            size: 'large',
          });
        } else {
          throw new Error("Google Identity Services SDK not available");
        }
      })
      .catch((err) => {
        console.error("Failed to load Google Identity Services", err);
        showError("Unable to load Google sign-in. Please try again later.");
      });

    return () => {
      const scriptTag = document.querySelector(`script[src="${src}"]`);
      if (scriptTag) document.body.removeChild(scriptTag);
    };
  }, [showError]);

  const handleCredentialResponse = async (response) => {
    if (!response.credential) return;

    try {
      setState(prev => ({ ...prev, submitted: true }));
      
      const googleResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/social_auth/google/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ auth_token: response.credential }),
        }
      );

      const data = await googleResponse.json();

      if (!googleResponse.ok) {
        throw new Error(data.detail || data.error || "Google login failed");
      }

      if (data.tokens?.access) {
        localStorage.setItem("user", data.username);
        localStorage.setItem("email", data.email);
        localStorage.setItem("group", data.group);
        localStorage.setItem("token", data.tokens.access);
        localStorage.setItem("google", "google");
        window.location.href = "/app/dashboard";
      } else {
        throw new Error(data.email?.[0] || data.detail || "Google login failed");
      }
    } catch (error) {
      console.error("Google login error:", error);
      showError(error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>LabAsService</title>
      </Helmet>
      <RemoveScrollBar />
      {state.submitted && !state.errorMessage ? (
        <Progress />
      ) : (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
            width: "100%",
            minHeight: "100vh",
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            style={{
              paddingTop: "60px",
              paddingRight: "30px",
              position: "fixed",
            }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Alert onClose={handleClose} severity="error">
              {state.errorMessage}
            </Alert>
          </Snackbar>
          {/* Loading overlay - will appear centered on top of everything */}
          {/* {state.submitted && !state.errorMessage && <Progress />} */}
          {/* Rest of your login form */}
          {!state.submitted && (
            <Box px={1} width="100%" height="100vh" mx="auto" position="relative" zIndex={2}>
              <Grid container spacing={1} justifyContent="center" alignItems="center" height="100%">
                <Grid item xs={11} sm={9} md={5} lg={4} xl={3}>
                  <Card>
                    <Box
                      variant="gradient"
                      bgColor="info"
                      borderRadius="lg"
                      coloredShadow="info"
                      mx={2}
                      mt={1}
                      p={-3}
                      mb={-2}
                      textAlign="center"
                    >
                      <img
                        style={{
                          width: "50%",
                          height: "50%",
                          marginLeft: "auto",
                          marginRight: "auto",
                          display: "block",
                        }}
                        alt="LabAsService"
                          src={logoImage}
                          // src={require("../../../assets/img/labasservice1.png").default}
                      />

                      <Typography variant="h4" fontWeight="medium" color="black" mt={1} mb={1} style={{ marginBottom: "-15px" }}>
                        Sign in with
                      </Typography>
                      
                      <Grid container spacing={2} justifyContent="center" sx={{ mt: 1, mb: 1 }}>
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                          <div id='google-login-btn'>
                            <div ref={googleButton} id='google-ref'></div>
                          </div>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Divider
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontWeight: "medium",
                              fontSize: "2px",
                              backgroundColor: "black",
                              width: "80%",
                              marginLeft: "40px",
                            }}
                          />
                          
                          <Typography variant="h6" fontWeight="medium" color="black" mt={1} style={{ marginLeft: "20px", marginBottom: "10px" }}>
                            Sign in with Credentials
                          </Typography>
                          
                          <CardBody>
                            <Grid container spacing={1}>
                              <Grid item md={12} xs={6} key="email">
                                <TextField
                                  variant="outlined"
                                  margin="normal"
                                  required
                                  fullWidth
                                  id="email"
                                  name="email"
                                  autoComplete="email"
                                  placeholder="Email"
                                  autoFocus
                                  value={state.email}
                                  onChange={handleChange}
                                  onKeyDown={handleKeyPress}
                                  disabled={state.submitted}
                                />
                              </Grid>
                              
                              <Grid item md={12} xs={6} key="password">
                                <TextField
                                  variant="outlined"
                                  margin="normal"
                                  required
                                  fullWidth
                                  name="password"
                                  placeholder="Password"
                                  type="password"
                                  id="password"
                                  autoComplete="current-password"
                                  onChange={handleChange}
                                  onKeyDown={handleKeyPress}
                                  value={state.password}
                                  disabled={state.submitted}
                                />
                              </Grid>
                            </Grid>
                          </CardBody>
                          
                          <Box mt={1} mb={1}>
                            <Button
                              variant="contained"
                              style={{ color: "#ffffff", backgroundColor: "#e91e63" }}
                              size="lg"
                              onClick={handleSubmitClick}
                              disabled={state.submitted}
                            >
                              {state.submitted ? "Signing In..." : "Sign In"}
                            </Button>
                          </Box>
                          
                          <Grid container spacing={12}>
                            <Grid item xs={6}>
                              <Link
                                color="primary"
                                href="forgotpassword"
                                style={{ whiteSpace: 'nowrap', fontSize: "14px", padding: "20px" }}
                              >
                                Forgot password?
                              </Link>
                            </Grid>
                            <Grid item xs={6}>
                              <Link
                                color="primary"
                                href="register"
                                style={{ whiteSpace: 'nowrap', fontSize: "14px", padding: "20px" }}
                              >
                                Create new account
                              </Link>
                            </Grid>
                          </Grid>

                          <Grid container spacing={12}>
                            <Grid item xs={12}>
                              <Link
                                color="primary"
                                href="/connectivitytest"
                                style={{ whiteSpace: 'nowrap', fontSize: "14px", padding: "20px" }}
                              >
                                Connectivity Test
                              </Link>
                            </Grid>
                            
                          </Grid>

                          <div>
                            <Link
                              color="primary"
                              href="https://www.zippyops.com/contact-us"
                              target="_blank"
                              style={{ marginTop: "1rem", display: "block", fontSize: "14px" }}
                            >
                              Contact us
                            </Link>

                            <p
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontSize: "16px",
                                color: "#757575",
                                fontWeight: 400,
                              }}
                            >
                              &copy; {1900 + new Date().getYear()} ZippyOPS Consulting Services LLP
                            </p>
                          </div>

                          <div style={{ paddingBottom: "30px" }}></div>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </div>
      )}
    </>
  );
}